import { useCallback, useId } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { UseGreetingCardProps } from "../types/greeting-interfaces";
import { validateGreetingData } from "../utils/validateGreetings";
import useBatchUploadFiles from "./useBatchUploadFiles";
import { GreetingFormWithFileFields } from "./useGreetingSettings";

export const useGreetingCard = ({
    greetingIndex,
    updateGreetings,
    markCardAsChanged,
    clearCardUnsaved,
    markCardAsUploading,
    clearCardUploading,
}: UseGreetingCardProps) => {
    const { t } = useTranslation();
    const rawId = useId();
    const keyPrefix = rawId.replace(/^:/, ""); // 去除開頭的冒號
    const { trigger, getValues, setValue, watch } = useFormContext<GreetingFormWithFileFields>();
    const { uploadAllFiles, isUploading } = useBatchUploadFiles();

    const greeting = watch(`greetings.data.${greetingIndex}`);
    const dialogues = greeting?.dialogues || [];

    const generateSentenceKey = useCallback(() => {
        return `${keyPrefix}${Date.now()}-${Math.random().toString(36).substring(2, 4)}`;
    }, [keyPrefix]);

    const markAsChanged = useCallback(() => {
        markCardAsChanged(greetingIndex);
    }, [markCardAsChanged, greetingIndex]);

    const handleAddDialogue = useCallback(() => {
        const currentDialogues = getValues(`greetings.data.${greetingIndex}.dialogues`) || [];
        const newDialogue = {
            sentences: [
                {
                    key: generateSentenceKey(),
                    content: "",
                    face: "",
                    action: "",
                    audio_url: "",
                    updated_time: new Date().toISOString(),
                },
            ],
        };
        setValue(`greetings.data.${greetingIndex}.dialogues`, [...currentDialogues, newDialogue]);
        markAsChanged();
    }, [getValues, setValue, generateSentenceKey, markAsChanged, greetingIndex]);

    const handleRemoveDialogue = useCallback(
        (dialogueIndex: number) => {
            const currentDialogues = getValues(`greetings.data.${greetingIndex}.dialogues`) || [];
            const updatedDialogues = currentDialogues.filter((_, index) => index !== dialogueIndex);
            setValue(`greetings.data.${greetingIndex}.dialogues`, updatedDialogues);
            markAsChanged();
        },
        [getValues, setValue, markAsChanged, greetingIndex]
    );

    const handleAddSentence = useCallback(
        (dialogueIndex: number) => {
            const currentSentences =
                getValues(`greetings.data.${greetingIndex}.dialogues.${dialogueIndex}.sentences`) || [];
            const newSentence = {
                key: generateSentenceKey(),
                content: "",
                face: "",
                action: "",
                audio_url: "",
                updated_time: new Date().toISOString(),
            };
            setValue(`greetings.data.${greetingIndex}.dialogues.${dialogueIndex}.sentences`, [
                ...currentSentences,
                newSentence,
            ]);
            markAsChanged();
        },
        [getValues, setValue, generateSentenceKey, markAsChanged, greetingIndex]
    );

    const handleRemoveSentence = useCallback(
        (dialogueIndex: number, sentenceIndex: number) => {
            const currentSentences =
                getValues(`greetings.data.${greetingIndex}.dialogues.${dialogueIndex}.sentences`) || [];
            const updatedSentences = currentSentences.filter((_, index) => index !== sentenceIndex);
            setValue(`greetings.data.${greetingIndex}.dialogues.${dialogueIndex}.sentences`, updatedSentences);
            markAsChanged();
        },
        [getValues, setValue, markAsChanged, greetingIndex]
    );

    const handleSaveChanges = useCallback(async () => {
        try {
            const isValid = await trigger(`greetings.data.${greetingIndex}`);
            if (!isValid) {
                customToast.warn(t("APPWIDGET.GREETINGS_SENTENCE_WARN") || "必須填寫語言、內容與音檔連結");
                return;
            }

            markCardAsUploading(greetingIndex);

            const currentData = getValues();
            const dataWithUrls = await uploadAllFiles<GreetingFormWithFileFields>(currentData);

            const invalidCards: Array<{ index: number; reason: string }> = [];
            const validCardIndices: number[] = [];
            const validGreetings = dataWithUrls.greetings.data.filter((g, idx) => {
                const cardNumber = idx + 1;

                if (!g.languages || g.languages.length === 0) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_LANGUAGE", { index: cardNumber }) ||
                            `對話組-${cardNumber}：未選擇語言`,
                    });
                    return false;
                }

                const hasValidContent = g.dialogues.some((d) => d.sentences.some((s) => s.content?.trim()));

                if (!hasValidContent) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_CONTENT", { index: cardNumber }) ||
                            `對話組-${cardNumber}：招呼詞內容為空`,
                    });
                    return false;
                }

                const hasInvalidAudioUrl = g.dialogues.some((d) =>
                    d.sentences.some((s) => s.content?.trim() && !s.audio_url?.trim())
                );

                if (hasInvalidAudioUrl) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_AUDIO_URL", { index: cardNumber }) ||
                            `對話組-${cardNumber}：音檔連結為空`,
                    });
                    return false;
                }

                validCardIndices.push(idx);
                return true;
            });

            if (invalidCards.length > 0) {
                const errorMessages = invalidCards.map((card) => card.reason).join("；");
                customToast.warn(errorMessages);
                return;
            }

            const validationResult = validateGreetingData(validGreetings);
            if (!validationResult.isValid) {
                customToast.error(validationResult.errorMessage);
                return;
            }

            const apiPayload = {
                enabled: dataWithUrls.greetings.enabled,
                data: validGreetings.map((g) => ({
                    languages: g.languages,
                    dialogues: g.dialogues.map((dialogue) => ({
                        sentences: dialogue.sentences.map((sentence) => ({
                            key: sentence.key,
                            content: sentence.content,
                            face: sentence.face || "",
                            action: sentence.action || "",
                            audio_url: sentence.audio_url,
                            updated_time: sentence.updated_time || new Date().toISOString(),
                        })),
                    })),
                })),
            };
            await updateGreetings(apiPayload);
            validCardIndices.forEach((idx) => {
                clearCardUnsaved(idx);
            });

            customToast.success(t("APPWIDGET.GREETINGS_SENTENCE_SAVED_SUCCESS") || "招呼詞已保存");
        } catch (error) {
            if (error instanceof Error && error.message.includes("Failed to upload")) {
                return;
            }
            customToast.error(t("APPWIDGET.GREETINGS_SENTENCE_ERROR") || "無法保存招呼詞，請重試");
        } finally {
            // Clear uploading state regardless of success or failure
            clearCardUploading(greetingIndex);
        }
    }, [
        trigger,
        getValues,
        uploadAllFiles,
        greetingIndex,
        t,
        updateGreetings,
        clearCardUnsaved,
        markCardAsUploading,
        clearCardUploading,
    ]);

    return {
        greeting,
        dialogues,
        handleAddDialogue,
        handleRemoveDialogue,
        handleAddSentence,
        handleRemoveSentence,
        handleSaveChanges,
        markAsChanged,
        isUploading,
    };
};
